import type {
  DetailField,
  DetailSection,
  FormConfig,
  TransportConfig,
} from "./types";

/** How each half of the Lotgen catalogue is named to people — the arrival form each one feeds. */
export const LOTGEN_SCOPE_LABELS: Record<
  NonNullable<FormConfig["lotgenScope"]>,
  string
> = {
  trading: "Trading",
  production: "PDN",
};

export const fullTransport: TransportConfig = {
  enabled: true,
  fields: [],
  vehicleValidation: "enforce",
  vehicleFormats: [],
};
/**
 * Fresh Seed Arrival's transport block (p66): Transport Name / Lorry Receipt No. / Vehicle No. /
 * Payment Mode under "Transport", the courier and hand-delivery fields under the other two modes.
 * No `gate_pass` — the screen has no such field and the form is not gate-raisable — and no
 * `dc_date` / `dc_number`, which this form carries in its header block instead.
 */
export const pdnTransport: TransportConfig = {
  enabled: true,
  fields: [
    "transport_name",
    "lorry_receipt_number",
    "vehicle_number",
    "payment_mode",
    "courier_name",
    "docket_no",
    "delivery_person_name",
  ],
  vehicleValidation: "enforce",
  vehicleFormats: [],
};
const base = {
  docType: "arrival" as const,
  sourceKind: "vendor" as const,
  destKind: "storage" as const,
  numberPrefix: "ARR",
  numberPad: 4,
  receivedCaptured: true,
  // Typed, not weighed. Only `arrival_scapture` is bag-wise today — see that form.
  bagWise: false,
  qaRequired: true, // A10
  transport: fullTransport,
  dcInfo: false,
};

/**
 * The Fresh Seed Arrival post item form (p66), block for block as the screen shows it. The first
 * block is everything "Get Details" pulls forward off the PDN; the operator types only Harvest Date
 * there.
 */
const pdnDetailSectionsWith = (moisture: DetailField): DetailSection[] => [
  {
    blocks: [
      {
        kind: "fields",
        fields: [
          { key: "organiser", readOnlyWhen: "untilLookup", fillRow: true },
          { key: "pdn_no", readOnlyWhen: "untilLookup" },
          { key: "pdn_date", readOnlyWhen: "untilLookup" },
          { key: "spc_f", readOnlyWhen: "untilLookup" },
          { key: "spc_m", readOnlyWhen: "untilLookup" },
          { key: "crop", readOnlyWhen: "untilLookup" },
          { key: "variety", readOnlyWhen: "untilLookup" },
          { key: "production_location", readOnlyWhen: "untilLookup" },
          { key: "production_person", readOnlyWhen: "untilLookup" },
          { key: "farmer", readOnlyWhen: "untilLookup" },
          { key: "plot_no", readOnlyWhen: "untilLookup" },
          { key: "harvest_date", required: true },
        ],
      },
      {
        kind: "matrix",
        columns: ["PDN", "Actual", "Difference"],
        rows: [
          {
            label: "No. of Bags (NoB)",
            cells: ["declared_bags", "received_bags", "difference_bags"],
          },
          {
            label: "Quantity",
            cells: ["declared_qty", "received_qty", "difference"],
            // "Tare Weight" shares the Actual cell: Actual Qty is gross, net = gross - tare.
            extra: { key: "tare_weight", column: 1 },
          },
        ],
      },
    ],
  },
  {
    title: "Preliminary Quality",
    blocks: [
      {
        kind: "fields",
        fields: [
          moisture,
          { key: "pp_status", required: true },
          // Read-only: the lot's QC state is decided by QA, not typed at the desk. It is shown
          // here, beside the arrival's own quality verdicts, because that is the one screen where
          // the operator can see both at once.
          { key: "qc_status", readOnlyWhen: "always" },
          { key: "le_months", suffix: "Months" },
          {
            key: "le_date",
            readOnlyWhen: "always",
            suffix: "From Harvest Date",
          },
        ],
      },
    ],
  },
  {
    title: "Grow Out Test (GOT)",
    blocks: [
      {
        kind: "fields",
        fields: [
          // VNR captures only the type at arrival; the sample flags and the GOT status were
          // seedtrac fields nothing here writes, so `got_status` stays a GRID column.
          { key: "got_type", required: true },
        ],
      },
    ],
  },
  {
    blocks: [
      {
        kind: "fields",
        fields: [
          { key: "stage", required: true },
          { key: "seed_status" },
          { key: "lot_number", readOnlyWhen: "always" },
          { key: "production_grade", required: true },
        ],
      },
    ],
  },
  { title: "Storage Location (SLOC)", blocks: [{ kind: "sloc" }] },
];
const pdnDetailSections = pdnDetailSectionsWith({
  key: "moisture_status",
  required: true,
});

/**
 * Production seed arriving against a PDN. Fields provisional until D1.
 *
 * Fed from the PRODUCTION half of the Lotgen catalogue (`trading_arrival` blank), which is where the
 * SP pair and the organiser / farmer / production-location provenance this form prints come from.
 * It used to read `pdn_inbox` with `source='pdn'` -- rows nothing ever wrote, so the picker was
 * empty.
 */
export const arrivalPdn: FormConfig = {
  ...base,
  code: "arrival_pdn",
  title: "Fresh Seed Arrival with PDN",
  // One series with Arrival with S-Capture (user, 2026-10-14: "the txn id would be continued in
  // both of them in the same sequence, no separate AF series"): the counter is keyed on the prefix,
  // so sharing "ASC" is what makes PDN and S-Capture arrivals number one after the other. Arrivals
  // numbered AF/n before this keep their numbers.
  numberPrefix: "ASC",
  numberPad: 1,
  declaredLabel: "PDN Qty",
  declaredFeed: true,
  lotSource: "lotgen",
  lotgenScope: "production",
  // seedtrac's PDN flow: the tablet raises the setup (Dispatch Date, DC Date, DC No.), then each lot
  // is one Lotgen PDN lot plus the DC's typed Bag(s) and Qty -- Lotgen's own figures are not used.
  raisedOnHandheld: true,
  declaredTypedAtSetup: true,
  allowManualLots: false,
  // Weighed sack by sack on the handheld, like S-Capture. Set here and not on `base`, which is also
  // spread into Stock Transfer — that form still records one typed Actual pair per lot.
  bagWise: true,
  // No bag scan (user, 2026-09-19): a PDN's sacks carry no barcode, so the operator picks the lot
  // and weighs each sack on the scale.
  // The Trading flow from here (user, 2026-09-18): the floor previews the unloading, the desk checks
  // the sheet or sends chosen lots back for reweighing, then bins and submits.
  checkStep: true,
  blindCoded: true,
  multipleMaterials: true,
  enforceUniqueLotNumber: true,
  transport: pdnTransport,
  headerFields: ["arrival_date", "dispatch_date", "dc_date", "dc_number"],
  requiredHeaderFields: ["dispatch_date", "dc_date"],
  defaultLeMonths: 36,
  // The grid is the READ-BACK list (p66); capture happens in `detailSections` below. Provenance
  // (organiser / farmer / plot / location / personnel) and Seed Status moved there — they are still
  // written through `lineInput`, so the PHSRN loses nothing.
  lineColumns: [
    "crop",
    "variety",
    "spc_f",
    "spc_m",
    "lot_number",
    "declared_bags",
    "declared_qty",
    "received_bags",
    "received_qty",
    "difference_bags",
    "difference",
    "stage",
    "moist_pct",
    "pp",
    "qc_status",
    "got",
    "production_grade",
    "storage",
    "actions",
  ],
  lineGroups: [
    { label: "PDN", columns: ["declared_bags", "declared_qty"] },
    { label: "Actual", columns: ["received_bags", "received_qty"] },
    { label: "Difference", columns: ["difference_bags", "difference"] },
    // seedtrac heads these "Moist % | PP" (p66), so the grid shows the feed's percentages; the
    // Post Item Form captures the Acceptable / Not Acceptable verdicts separately.
    { label: "Preliminary QC", columns: ["moist_pct", "pp"] },
  ],
  detailSections: pdnDetailSections,
  documents: ["frn", "unloading_sheet"],
  // S-Capture inherits it; the other arrival forms send no Unloading Sheet mail (user, 2026-10-22).
  emailsUnloadingSheet: true,
  provisional: true,
};

/** Production seed arriving from an S-Capture dispatch (per-bag declared weights). Provisional until D1. */
export const arrivalScapture: FormConfig = {
  ...arrivalPdn,
  code: "arrival_scapture",
  title: "Arrival with S-Capture",
  numberPrefix: "ASC",
  lotSource: "scapture",
  // Cleared, not inherited: S-Capture is its own feed, not a half of the Lotgen catalogue.
  lotgenScope: undefined,
  // Also cleared: S-Capture books a whole feed-announced truck, desk or handheld, with the feed's own
  // declared figures -- none of PDN's tablet setup or typed DC pair applies.
  raisedOnHandheld: undefined,
  declaredTypedAtSetup: undefined,
  // Scan-only (user, 2026-10-06): every sack comes declared with its barcode, so the scan is the
  // ONLY way to weigh one -- no lot tap, no lot label. A torn label is reprinted from the web's
  // "Print bag barcodes"; a lot the feed sent without barcodes is not weighed on the handheld.
  bagBarcodeRequired: true,
  // The Trading flow, as PDN (user, 2026-09-18: "scapture also contain back to re weighing"): the desk
  // checks the unloading sheet or sends chosen lots back to be weighed again, then bins and submits.
  checkStep: true,
  // The desk types the DC Date off the challan after the floor hands the truck over (user, 2026-10-21).
  previewEditableHeaderFields: ["dc_date", "dc_number"],
  // Booking fills DC Date with the truck's dispatch date and a missing DC No. with "0" (user,
  // 2026-10-21); both stay the desk's to correct.
  dcFromDispatch: true,
  // The one bag-wise form: S-Capture is the feed that carries per-bag declared weights
  // (`subsub_records`: lotno, barcode, weight), so it is where weighing every sack against a
  // declaration actually means something. The handheld owns that capture; the web reviews it.
  bagWise: true,
  // S-Capture's moisture is whatever the feed sent (user, 2026-09-23): its % (disabled), else its word
  // ("Normal", "High"), else the Acceptable / Not Acceptable dropdown, prefilled Acceptable.
  detailSections: pdnDetailSectionsWith({
    key: "moisture_status",
    required: true,
    feedReading: true,
  }),
  provisional: true,
};

/** Bought-in seed: lots from the Lotgen catalogue or typed by hand, declared off the vendor's challan. */
export const arrivalTrading: FormConfig = {
  ...base,
  code: "arrival_trading",
  title: "Trading Arrival",
  numberPrefix: "AT",
  numberPad: 1,
  declaredLabel: "DC Qty",
  declaredFeed: true,
  lotSource: "lotgen",
  lotgenScope: "trading",
  allowManualLots: true,
  // The desk finalizes the setup; the truck reaches the handheld only then (user, 2026-10-29).
  setupOnDesk: true,
  // The desk types As Per DC; the handheld weighs every sack against it. Actual is derived from the
  // bags, never typed — see `typedTotalsFor`.
  bagWise: true,
  // Unloaded by (floor, Preview Unloading) → Checked by (desk, Mark Checked, or send lots back for
  // reweighing) → SLOC updated by (desk, bins then submits). User, 2026-09-18.
  checkStep: true,
  blindCoded: false,
  multipleMaterials: true,
  enforceUniqueLotNumber: true,
  dcInfo: true,
  // Crop / Variety / Vendor Variety / No. of Lots / Stage sit in the HEADER on this form: a trading
  // arrival is one crop per document, which is why TRN-I and TRN-P print a single Crop row.
  headerFields: [
    "dc_date",
    "dc_number",
    "party",
    "order_ref_no",
    "address",
    "header_crop",
    "header_variety",
    "header_vendor_variety",
    "no_of_lots",
    "header_stage",
  ],
  // The lots take their variety from the header, so the desk cannot go on to them without one
  // (user, 2026-09-21). Picking a variety needs a crop first, so this holds the crop too. Stage is
  // required as well: it is inherited by every lot (user, 2026-09-29).
  requiredHeaderFields: ["header_variety", "header_stage"],
  lineColumns: [
    "vendor_lot_no",
    "lot_number",
    "declared_bags",
    "declared_qty",
    "received_bags",
    "received_qty",
    // Actual is weighed GROSS and Difference is net of tare: without it a lot reading DC 980 /
    // Actual 994 / Difference 0 looks like an arithmetic error (as the Trading page showed it).
    "tare_weight",
    "difference_bags",
    "difference",
    "moist_pct",
    // PP is the Acceptable / Not Acceptable verdict here, prefilled Acceptable, and GOT Status is
    // GOT-R / GOT-NR (user, 2026-09-29) -- not the PP % or the UT / OK / Fail status.
    "pp_status",
    "qc_status",
    "got_type",
    "storage",
    "actions",
  ],
  columnLabels: { pp_status: "PP", got_type: "GOT Status" },
  // Every lot field the desk fills is mandatory (user, 2026-09-29); QC Status stays the system's UT.
  requiredLineFields: [
    "vendor_lot_no",
    "declared_bags",
    "declared_qty",
    "moist_pct",
    "pp_status",
    "got_type",
  ],
  lineGroups: [
    { label: "As Per DC", columns: ["declared_bags", "declared_qty"] },
    {
      label: "Actual",
      columns: ["received_bags", "received_qty", "tare_weight"],
    },
    { label: "Difference", columns: ["difference_bags", "difference"] },
    { label: "Preliminary QC", columns: ["moist_pct", "pp_status"] },
  ],
  // No FRN: the TRN-I / TRN-P are Trading's receipt notes (user, 2026-09-21, reversing 2026-09-14).
  // The Unloading Sheet stays, as on every weighed arrival.
  documents: ["unloading_sheet", "trn_i", "trn_p"],
};

/**
 * Stock Transfer In: seed arriving from another VNR plant (seedtrac "Arrival Stock Transfer - Plant",
 * manual p69–70) on a submitted Dispatch Stock Transfer – Plant (STDN).
 *
 * A RECEIPT, not a fresh arrival: the lot keeps its number across plants, so this mints nothing. The
 * handheld raises it from a truck the gate has let in; its lines are the STDN's (lot, crop, variety,
 * stage and the dispatched NoB/Qty, all read-only), the bags are weighed on the handheld, and Final
 * Submit closes the dispatched quantity out of transit into this plant's bins. Any shortfall is the
 * transfer's loss. QC sampling is raised again at the receiving plant (user, 2026-09-17).
 */
export const arrivalStockTransfer: FormConfig = {
  ...base,
  code: "arrival_stock_transfer",
  title: "Stock Transfer In",
  numberPrefix: "AS",
  numberPad: 1,
  receivesFrom: "dispatch_stock_transfer",
  // The receipt is started on the handheld from a truck the gate let in; the desk has no New.
  raisedOnHandheld: true,
  // Weighed bag by bag on the handheld, like S-Capture; the desk checks, previews and submits.
  bagWise: true,
  checkStep: true,
  // ...unless whoever opens it says no: then each lot's quantity and bags are typed on the web
  // (user, 2026-09-19: "per bag weighing is optional").
  weighingChoice: true,
  declaredLabel: "Dispatch",
  declaredFeed: true,
  lotSource: "manual",
  // Its lots are the STDN's: none is added by hand.
  allowManualLots: false,
  blindCoded: false,
  multipleMaterials: true,
  enforceUniqueLotNumber: true,
  headerFields: ["source_plant", "address"],
  // The sending plant and its address come from the STDN.
  lockedHeaderFields: ["source_plant", "address"],
  // Plain "Date" on a receipt (user, 2026-10-15).
  headerLabels: { arrival_date: "Date" },
  // No DC No. / DC Date: the STDN is the paperwork (user, 2026-10-15).
  transport: {
    ...fullTransport,
    fields: [
      "transport_name",
      "lorry_receipt_number",
      "vehicle_number",
      "payment_mode",
      "courier_name",
      "docket_no",
      "delivery_person_name",
    ],
  },
  lineColumns: [
    "crop",
    "variety",
    "lot_number",
    "declared_bags",
    "declared_qty",
    "received_bags",
    "received_qty",
    "difference_bags",
    "difference",
    "stage",
    "quality_status",
    "pp",
    "moist_pct",
    "germ_pct",
    "got_type",
    "qc_status",
    "storage",
    "actions",
  ],
  lineGroups: [
    { label: "Dispatch", columns: ["declared_bags", "declared_qty"] },
    { label: "Received", columns: ["received_bags", "received_qty"] },
    { label: "Difference", columns: ["difference_bags", "difference"] },
  ],
  // Everything about the lot is the STDN's and the sending plant's: read-only here.
  detailSections: [
    {
      blocks: [
        {
          kind: "fields",
          fields: [
            { key: "lot_number", readOnlyWhen: "always" },
            { key: "crop", readOnlyWhen: "always" },
            { key: "variety", readOnlyWhen: "always" },
            { key: "stage", readOnlyWhen: "always" },
          ],
        },
        {
          kind: "matrix",
          columns: ["Dispatch", "Received", "Difference"],
          rows: [
            {
              label: "No. of Bags (NoB)",
              cells: ["declared_bags", "received_bags", "difference_bags"],
            },
            {
              label: "Quantity",
              cells: ["declared_qty", "received_qty", "difference"],
            },
          ],
        },
      ],
    },
    {
      title: "Preliminary Quality",
      blocks: [
        {
          kind: "fields",
          fields: [
            { key: "moist_pct", readOnlyWhen: "always" },
            { key: "pp", readOnlyWhen: "always" },
            { key: "germ_pct", readOnlyWhen: "always" },
            { key: "got_type", readOnlyWhen: "always" },
            { key: "quality_status", readOnlyWhen: "always" },
            { key: "qc_status", readOnlyWhen: "always" },
          ],
        },
      ],
    },
    { title: "Storage Location (SLOC)", blocks: [{ kind: "sloc" }] },
  ],
  // The Unloading Sheet prints the handheld's bag weights; a receipt typed per lot has none, so
  // `formFor` takes it off one of those.
  documents: ["strn", "unloading_sheet"],
};
